import { useEffect, useRef, useState, type FC } from "react";
import { Page, PageContext, PageOptions } from "../types/page";
import storageService from "../core/services/storageService";
import LandingPage from "../pages/LandingPage";
import CustomerInquiryPage from "../pages/CustomerInquiryPage";
import ConversationPage from "../pages/ConversationPage";
import CustomMainPage from "../pages/CustomMainPage";
import ErrorPage from "../pages/ErrorPage";
import LoginPage from "../pages/LoginPage";
import EnterCodePage from "../pages/EnterCodePage";
import ChatHistoryPage from "../pages/ChatHistoryPage";
import { useWidget } from "../contexts/useWidget";
import { useCore } from "../contexts/useCore";
import { useCoreState } from "../contexts/useCoreState";
import { useThreadAutoNavigate } from "../contexts/useThreadAutoNavigate";
import { usePanelInteractiveAck } from "../contexts/usePanelInteractiveAck";
import type { LastBotState } from "../core/state";

/**
 * State exposed on the chat container as `data-lastbot-*` attributes so embedders
 * can style different states purely from CSS. See docs/api.md § State attributes.
 *
 * Note: `data-lastbot-state` (open / closed / full-screen / hidden) is set higher
 * up on a persistent wrapper in App.tsx so it's queryable even when the chat panel
 * is not rendered.
 */
type WidgetStateSlice = {
  threadState: "empty" | "active" | "closed";
  connection: LastBotState["connectionStatus"];
  agentTyping: boolean;
  authenticated: boolean;
};
const widgetStateSelector = (state: LastBotState): WidgetStateSlice => ({
  threadState:
    state.thread == null
      ? "empty"
      : state.thread.closedAt != null
        ? "closed"
        : "active",
  connection: state.connectionStatus,
  agentTyping: state.agents.typing,
  authenticated: state.contact?.verified === true,
});

const PAGE_TO_KEBAB: Record<Page["type"], string> = {
  landing: "landing",
  customerInquiry: "customer-inquiry",
  conversation: "conversation",
  chatHistory: "chat-history",
  login: "login",
  enterCode: "enter-code",
  errorPage: "error-page",
};

interface ChatWidgetProps {
  isFullscreen: boolean;
  /** When true, the chat renders in-flow filling its parent (no fixed positioning, no overlay shadow). */
  inline?: boolean;
}

const ChatWidget: FC<ChatWidgetProps> = ({ isFullscreen, inline = false }) => {
  const { startMessage, hasCustomMainPage, storageScope } = useWidget();
  const core = useCore();
  const [page, setPage] = useState<Page>(() => storageService.getPage(storageScope));
  const [pageContext, setPageContext] = useState<PageContext | null>(startMessage ? { message: startMessage } : null);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetState = useCoreState(core, widgetStateSelector);

  const navigate = (pageOptions: PageOptions): void => {
    storageService.setPage(storageScope, pageOptions.page);
    setPage(pageOptions.page);
    setPageContext(pageOptions.pageContext ?? null);
  };

  // When the embedder declared `lastbot-custom-main-page`, rehydrate a thread
  // from a prior multi-page session at mount: if sessionStorage had a
  // `conversation` page with a messageThreadId, open it via `core.openThread`
  // so the single-page surface comes up with the conversation already loaded.
  // Failures (404 — thread gone, 499 — aborted) are swallowed; the user simply
  // lands on a fresh main page with no thread.
  useEffect(() => {
    if (!hasCustomMainPage) return;
    const stored = storageService.getPage(storageScope);
    if (
      stored.type === "conversation" &&
      stored.params?.messageThreadId !== undefined
    ) {
      const threadId = parseInt(stored.params.messageThreadId, 10);
      if (Number.isFinite(threadId)) {
        void core.openThread(threadId).catch(() => undefined);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-navigate to the conversation page when a NEW thread becomes active
  // while another page is showing — e.g. an embedder calls core.open() +
  // core.sendMessage() in the same tick, or core.startNewThread() from a
  // host-page button. State-derived (LAS-1491): the old thread:opened
  // listener here could miss an event that fired between this component's
  // mount and its effect subscription. Threads that already existed at mount
  // are covered by the mount-time storage read instead (useThreadPagePersistence
  // keeps the stored page in sync — LAS-1492), so the hook only reacts to
  // post-mount thread transitions and never yanks a user who deliberately
  // navigated elsewhere (history, login, back to landing) into the
  // conversation. In-UI flows (starters, history selection) navigate BEFORE
  // opening the thread, so they're already on `conversation` and skipped.
  // In `hasCustomMainPage` mode no navigation is needed — CustomMainPage
  // renders thread state in place via core.subscribe().
  useThreadAutoNavigate(core, page.type, navigate, hasCustomMainPage);

  // v2 nav events. Interactive slots (login-button, history-button) call the
  // matching core method which emits these events; the default UI listens
  // here and performs the navigation. Headless embedders own routing and
  // Capture the page-before-login when nav:login fires from core (driven by
  // either the login-button slot's runSlotAction or an embedder calling
  // core.showPage("login") directly). The actual navigate-to-login happens inside
  // the listener registered below.
  const captureLoginReturn = (): void => {
    const currentPage = storageService.getPage(storageScope);
    const ignoredPage =
      ["errorPage", "enterCode", "login"].includes(currentPage.type) ||
      (currentPage.type === "conversation" && !currentPage.params);
    const pageBeforeLogin = ignoredPage ? storageService.getPageBeforeLogin(storageScope) : currentPage;
    storageService.setPageBeforeLogin(storageScope, pageBeforeLogin);
  };

  // can intercept the events themselves (or simply not use core.showPage
  // at all).
  useEffect(() => {
    const onNavLogin = (): void => {
      captureLoginReturn();
      navigate({ page: { type: "login" } });
    };
    const onNavHistory = (): void =>
      navigate({ page: { type: "chatHistory" } });
    const onNavLanding = (): void => navigate({ page: { type: "landing" } });
    core.on("nav:login", onNavLogin);
    core.on("nav:history", onNavHistory);
    core.on("nav:landing", onNavLanding);
    return () => {
      core.off("nav:login", onNavLogin);
      core.off("nav:history", onNavHistory);
      core.off("nav:landing", onNavLanding);
    };
    // captureLoginReturn / navigate close over module-scoped storageService
    // and setPage. Stable across renders.
  }, [core]);

  // Readiness ack for the promise API (LAS-2166): flips
  // state.ui.panelInteractive true once this component's effects have
  // flushed. MUST stay declared after the nav:* listener effect above —
  // effects run in declaration order, and `await core.open()` resolving
  // before those listeners attach would reintroduce the lost-nav-command
  // race this hook exists to close.
  usePanelInteractiveAck(core);

  // Positioning/sizing lives in the `.lastbot-chat-overlay` / `.lastbot-chat-inline`
  // rules in index.css (keyed off data-lastbot-state); here we just pick the hook.
  const containerClasses = `${inline ? "lastbot-chat-inline" : "lastbot-chat-overlay"} flex flex-col bg-lastbot-surface`;

  // State attributes for CSS targeting from embedder templates / styles.
  // In single-page mode the page id collapses to "custom-main" so the
  // landing/customerInquiry/conversation cycle reads as one logical page.
  const isCollapsedToCustomMain =
    hasCustomMainPage &&
    (page.type === "landing" ||
      page.type === "customerInquiry" ||
      page.type === "conversation");
  const pageAttr = isCollapsedToCustomMain
    ? "custom-main"
    : PAGE_TO_KEBAB[page.type];

  return (
    <div
      ref={containerRef}
      className={containerClasses}
      data-testid={`chat-widget`}
      data-lastbot-page={pageAttr}
      data-lastbot-thread-state={widgetState.threadState}
      data-lastbot-connection={widgetState.connection}
      data-lastbot-agent-typing={widgetState.agentTyping ? "true" : "false"}
      data-lastbot-authenticated={widgetState.authenticated ? "true" : "false"}
    >
      {hasCustomMainPage &&
        (page.type === "landing" ||
          page.type === "customerInquiry" ||
          page.type === "conversation") && (
          <CustomMainPage
            containerRef={containerRef}
            navigate={navigate}
            isFullscreen={isFullscreen}
          />
        )}
      {!hasCustomMainPage && page.type === "landing" && (
        <LandingPage
          containerRef={containerRef}
          navigate={navigate}
          isFullscreen={isFullscreen}
        />
      )}
      {!hasCustomMainPage && page.type === "customerInquiry" && (
        <CustomerInquiryPage
          navigate={navigate}
          isFullscreen={isFullscreen}
        />
      )}
      {!hasCustomMainPage && page.type === "conversation" && (
        <ConversationPage
          params={page.params ?? {}}
          pageContext={pageContext}
          navigate={navigate}
          isFullscreen={isFullscreen}
        />
      )}
      {page.type === "chatHistory" && (
        <ChatHistoryPage
          navigate={navigate}
          isFullscreen={isFullscreen}
        />
      )}
      {page.type === "login" && (
        <LoginPage
          navigate={navigate}
          isFullscreen={isFullscreen}
        />
      )}
      {page.type === "enterCode" && (
        <EnterCodePage
          navigate={navigate}
          params={page.params!}
          isFullscreen={isFullscreen}
        />
      )}
      {page.type === "errorPage" && (
        <ErrorPage
          navigate={navigate}
          isFullscreen={isFullscreen}
        />
      )}
    </div>
  );
};

export default ChatWidget;
